import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Chip, Typography } from "@mui/material";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import { debounce } from "lodash";
import { fetchSubRoles } from "redux/actions/RolesAndPermissionsAction";
import CommonSubHeader from "components/commonSubHeader/CommonSubHeader";
import CommonTable from "components/commonTable/CommonTable";
import Loader from "components/loader/Loader";
import SubRoleModal from "./SubRoleModal";

export default function SubRolesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // parentRole is passed via navigate state
  const { parentRole } = location.state || {};

  const { subRoles, loading } = useSelector((state) => state.rbacReducer);

  // Search & Pagination State
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubRole, setSelectedSubRole] = useState(null); // null = create, object = edit

  useEffect(() => {
    if (!parentRole?.id) {
      navigate("/roles-permissions");
      return;
    }
    dispatch(fetchSubRoles(parentRole.id));
  }, []);

  const handleBack = () => navigate("/roles-permissions");

  const handleAddSubRole = () => {
    setSelectedSubRole(null);
    setModalOpen(true);
  };

  const handleEditSubRole = (sub) => {
    setSelectedSubRole(sub);
    setModalOpen(true);
  };

  const handleModalSuccess = () => {
    if (parentRole?.id) {
      dispatch(fetchSubRoles(parentRole.id));
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const onSearch = debounce((e) => {
    setSearch(e);
    setPage(0);
  }, 500);

  const filteredSubRoles = subRoles.filter((sub) =>
    sub.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const paginatedSubRoles = filteredSubRoles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const tableData = {
    header: [
      { label: "Sr. No.", key: "srNo" },
      { label: "Sub Role Name", key: "name" },
      { label: "Platform", key: "platform" },
      { label: "Business Type", key: "businessType" },
      { label: "Role Type", key: "roleType" },
      { label: "Permissions", key: "permissions" },
    ],
    body: paginatedSubRoles.map((sub, index) => ({
      ...sub,
      srNo: page * rowsPerPage + index + 1,
    })),
    totalCount: filteredSubRoles.length,
  };

  return (
    <Box className="mb20">
      {/* ── Header & Info ─────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <ArrowBackIosRoundedIcon
          className="font24 colorGrey"
          sx={{ cursor: "pointer" }}
          onClick={handleBack}
        />
        <Typography variant="h5" component="h1" fontWeight="bold">
          Sub Roles
        </Typography>
      </Box>

      {parentRole && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mb: 3,
            p: 1.5,
            bgcolor: "#f8f9fa",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Chip
            label={`Parent Role: ${parentRole.name}`}
            size="small"
            variant="outlined"
            color="primary"
          />
          <Chip
            label={`Platform: ${parentRole.platform}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Business Type: ${parentRole.businessType}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Role Type: ${parentRole.roleType}`}
            size="small"
            variant="outlined"
          />
        </Box>
      )}

      {/* ── Subheader (Search & Add Button) ────────────────────────── */}
      <Box className="mb30">
        <CommonSubHeader
          filter={false}
          onSearchQuery={onSearch}
          search={search}
          showDateFilter={false}
          buttonName="Add New Sub Role"
          navigation={handleAddSubRole}
        />
      </Box>

      {/* ── Sub Roles Table ────────────────────────────────────────── */}
      {loading && !subRoles.length ? (
        <Loader height={"50vh"} />
      ) : (
        <CommonTable
          data={tableData}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          onViewUpdatePermissions={handleEditSubRole}
          tabName={"roles-permissions"}
        />
      )}

      {/* ── Sub Role Modal (Create / Edit) ───────────────────────── */}
      <SubRoleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        parentRole={parentRole}
        subRole={selectedSubRole}
        onSuccess={handleModalSuccess}
      />
    </Box>
  );
}
